import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, Switch, Text, TextInput, Stack, Title, Badge } from "@mantine/core";
import { IconArrowDown, IconArrowUp, IconPencil, IconTrash } from "@tabler/icons";
import { Page, FormSectionCard } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import _ from "lodash";
import PageListHeader from "../../components/pageListHeader";
import { showNotification } from "@mantine/notifications";
import { IconCircleCheck, IconExclamationMark } from "@tabler/icons";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "../../components/pageList";
import LocalPageList from "../../components/localPageList";
import ActionCellRender from "../../components/actionCellRender";
import { useTranslation } from "react-i18next";
import {
  normalizeSourceOfFundOptions,
  serializeSourceOfFundOptions,
  slugifySourceOfFundKey,
  validateSourceOfFundOptions,
} from "../../helpers/riskControlSettingsUtils";

const buildOptionFormSchema = (isEdit = false) => ({
  title: isEdit ? "Edit source of fund" : "Add source of fund",
  display: { mode: ENUM_FORM_DISPLAY.MODAL, size: "md" },
  initialValues: {
    label: "",
    requiresSpecify: false,
    enabled: true,
  },
  validate: {
    label: (value) => (String(value || "").trim() ? null : "Label is required"),
  },
  layout: {
    containers: [
      {
        key: "option-section",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          withBorder: false,
          withPaper: false,
          minHeight: 0,
        },
      },
    ],
    fields: [
      {
        name: "key",
        component: TextInput,
        parent: "option-section",
        props: {
          label: "Key",
          readOnly: true,
          disabled: true,
        },
      },
      {
        name: "label",
        component: TextInput,
        parent: "option-section",
        props: {
          label: "Label",
          required: true,
        },
      },
      {
        name: "requiresSpecify",
        component: Checkbox,
        parent: "option-section",
        props: {
          label: "Requires specify",
          type: "checkbox",
        },
      },
      {
        name: "enabled",
        component: Checkbox,
        parent: "option-section",
        props: {
          label: "Enabled",
          type: "checkbox",
        },
      },
    ],
  },
});

const buildOptionTableSchema = () => ({
  columns: [
    {
      field: "label",
      headerName: "Label",
      minWidth: 180,
    },
    {
      field: "key",
      headerName: "Key",
      minWidth: 140,
    },
    {
      field: "requiresSpecify",
      headerName: "Requires specify",
      minWidth: 120,
      cellRender: (_col, data) => (
        <Badge color={data.requiresSpecify ? "blue" : "gray"} variant="light">
          {data.requiresSpecify ? "Yes" : "No"}
        </Badge>
      ),
    },
    {
      field: "enabled",
      headerName: "Enabled",
      minWidth: 100,
      cellRender: (_col, data) => (
        <Badge color={data.enabled ? "green" : "gray"} variant="light">
          {data.enabled ? "Yes" : "No"}
        </Badge>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      isAction: true,
      minWidth: 140,
      cellRender: ActionCellRender,
      actions: [
        { name: "moveUp", label: "Move up", icon: IconArrowUp, props: {} },
        { name: "moveDown", label: "Move down", icon: IconArrowDown, props: {} },
        { name: "edit", label: "Edit", icon: IconPencil, props: {} },
        { name: "delete", label: "Delete", icon: IconTrash, props: {} },
      ],
    },
  ],
});

const RiskControlSetting = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const tabNav = useTabNavigate();
  const [settingId, setSettingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const tableSchema = useMemo(() => buildOptionTableSchema(), []);
  const formSchema = useMemo(() => buildOptionFormSchema(), []);

  const fetchSetting = async () => {
    try {
      setLoading(true);
      const result = await api.search({
        apiEntity: "setting",
        pageSize: 1,
      });

      if (!result || _.isEmpty(result.docs)) {
        setSettingId(null);
        setRows(normalizeSourceOfFundOptions());
        return;
      }

      const setting = result.docs[0];
      setSettingId(setting._id);
      setRows(normalizeSourceOfFundOptions(setting.riskControl?.sourceOfFundOptions));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSetting();
  }, []);

  const moveRow = (rowIndex, direction) => {
    const nextIndex = rowIndex + direction;
    if (nextIndex < 0 || nextIndex >= rows.length) return;
    const nextRows = [...rows];
    const [item] = nextRows.splice(rowIndex, 1);
    nextRows.splice(nextIndex, 0, item);
    setRows(normalizeSourceOfFundOptions(nextRows));
  };

  const handleRowsChange = (nextRows) => {
    const existingKeys = new Set(
      rows.map((row) => row.key).filter(Boolean)
    );
    const normalized = normalizeSourceOfFundOptions(
      nextRows.map((row, index) => {
        if (row.key) return row;
        const key = slugifySourceOfFundKey(row.label, existingKeys);
        existingKeys.add(key);
        return { ...row, key, sortOrder: index };
      })
    );
    setRows(normalized);
  };

  const handleSave = async () => {
    const validationError = validateSourceOfFundOptions(rows);
    if (validationError) {
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: validationError,
      });
      return;
    }

    try {
      setSaving(true);
      let result;
      const riskControl = {
        sourceOfFundOptions: serializeSourceOfFundOptions(rows),
      };

      if (settingId) {
        const existing = await api.getById({
          apiEntity: "setting",
          id: settingId,
        });
        result = await api.updateById({
          apiEntity: "setting",
          id: settingId,
          values: {
            ...existing,
            riskControl: {
              ...(existing.riskControl || {}),
              ...riskControl,
            },
          },
        });
      } else {
        result = await api.add({
          apiEntity: "setting",
          values: { riskControl },
        });
        if (result?.success && result?.data?._id) {
          setSettingId(result.data._id);
        }
      }

      if (result?.success) {
        showNotification({
          title: t("Save"),
          color: "green",
          icon: <IconCircleCheck size={18} />,
          message: t("Risk control settings saved."),
        });
        return;
      }

      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: api.getErrorMsg(result?.error) || t("Failed to save settings."),
      });
    } catch (error) {
      console.log(error);
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: t("Failed to save settings."),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <PageListHeader
        title={t("Risk Control")}
        actionButtons={[
          {
            label: t("Save"),
            onClick: handleSave,
            loading: saving,
            disabled: loading,
          },
          {
            label: t("Close"),
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />

      {!loading && (
        <Stack spacing="md">
          <FormSectionCard>
            <Title order={5}>{t("Source of Fund")}</Title>
            <Text size="xs" color="dimmed" mb="md">
              {t(
                "Configure the source of fund options shown on the client Risk Control form. Enable Requires specify to show a text field when the option is checked."
              )}
            </Text>
            <LocalPageList
              rows={rows}
              onFormSubmit={handleRowsChange}
              tableSchema={tableSchema}
              formSchema={formSchema}
              btnLabel="Add option"
              onActionBtnClick={({ action, rowIndex }) => {
                if (action === "moveUp") moveRow(rowIndex, -1);
                if (action === "moveDown") moveRow(rowIndex, 1);
              }}
            />
          </FormSectionCard>
        </Stack>
      )}
    </Page>
  );
};

export default RiskControlSetting;
